import { parseReportDefinitionMetrics, validateMetricQuestions } from './metricValidation';
import {
  duplicateMetricIdentifiers,
  metricDependencies,
  type MetricQuestion,
  type ReportMetricDefinition,
} from './types';

/**
 * Validation results per metric, reused across draft edits. A metric is validated again only when it or one of
 * its dependencies changed (or the questions did); typing in one metric no longer re-parses every other one.
 */
interface MetricErrorCache {
  questions?: readonly MetricQuestion[];
  entries: Map<string, { key: string; error?: string }>;
}

export const createMetricErrorCache = (): MetricErrorCache => ({ entries: new Map() });

/** An unfinished metric must not erase valid metrics elsewhere in the live preview. */
export function getMetricErrors(
  metrics: ReportMetricDefinition[],
  questions: readonly MetricQuestion[],
  cache: MetricErrorCache = createMetricErrorCache(),
): Record<string, string> {
  if (cache.questions !== questions) {
    cache.questions = questions;
    cache.entries.clear();
  }
  const errors: Record<string, string> = Object.create(null);
  const registry = new Map(metrics.map((metric) => [metric.id, metric]));
  const seen = new Set<string>();
  metrics.forEach((metric) => {
    const dependencies = new Set<string>();
    const visit = (id: string) => {
      if (dependencies.has(id)) {
        return;
      }
      dependencies.add(id);
      const calculation = registry.get(id)?.calculation;
      if (calculation) {
        metricDependencies(calculation).forEach(visit);
      }
    };
    visit(metric.id);
    const involved = metrics.filter((item) => dependencies.has(item.id));
    const key = JSON.stringify(involved);
    seen.add(metric.id);
    const cached = cache.entries.get(metric.id);
    if (cached && cached.key === key) {
      if (cached.error) {
        errors[metric.id] = cached.error;
      }
      return;
    }
    let error: string | undefined;
    try {
      validateMetricQuestions(parseReportDefinitionMetrics({ schemaVersion: 1, metrics: involved }), questions);
    } catch (caught) {
      error = (caught as Error).message;
    }
    cache.entries.set(metric.id, { key, error });
    if (error) {
      errors[metric.id] = error;
    }
  });
  // Deleted metrics must not keep stale entries alive.
  [...cache.entries.keys()].forEach((id) => {
    if (!seen.has(id)) {
      cache.entries.delete(id);
    }
  });
  return errors;
}

/** Kept apart from calculation errors: a clashing identifier blocks applying and saving, not the live preview. */
export function getMetricIdentifierErrors(metrics: ReportMetricDefinition[]): Record<string, string> {
  return Object.fromEntries(duplicateMetricIdentifiers(metrics).map((id) => [id, 'duplicateIdentifier']));
}
